import React from 'react';
import GroupedTable, { type ColumnGroup, type GroupedColumn } from '../../../../common/components/Table/GroupedTable';

export interface CN10ReportItem {
  officeOfOrigin: string;
  officeOfDestination: string;
  month: number;
  quarter: number;
  normalNetweight: number;
  normalNetweightFormatted: string;
  normalItems: number;
  normalItemsFormatted: string;
  bulkyNetWeight: number;
  bulkyNetWeightFormatted: string;
  bulkyItems: number;
  bulkyItemsFormatted: string;
  totalnetWeight: number;
  totalnetWeightFormatted: string;
  totalItems: number;
  totalItemsFormatted: string;
}

export interface CN10FileData {
  filename: string;
  total: {
    normalNetweight: string;
    normalNetweightFormatted: string;
    normalItems: string;
    normalItemsFormatted: string;
    bulkyNetWeight: string;
    bulkyNetWeightFormatted: string;
    bulkyItems: string;
    bulkyItemsFormatted: string;
    totalnetWeight: string;
    totalnetWeightFormatted: string;
    totalItems: string;
    totalItemsFormatted: string;
  };
  designatedOperator: string;
  reportItems: CN10ReportItem[];
  mailCategory: string;
}

export interface CN10TableProps {
  data: CN10ReportItem[];
  filename?: string;
  designatedOperator?: string;
  mailCategory?: string;
  totalData?: {
    normalNetweight: string;
    normalNetweightFormatted: string;
    normalItems: string;
    normalItemsFormatted: string;
    bulkyNetWeight: string;
    bulkyNetWeightFormatted: string;
    bulkyItems: string;
    bulkyItemsFormatted: string;
    totalnetWeight: string;
    totalnetWeightFormatted: string;
    totalItems: string;
    totalItemsFormatted: string;
  };
  isLoading?: boolean;
  rowBackgroundColor?: string | ((row: CN10ReportItem) => string);
}



// Define column groups for CN10 table structure
const createCN10ColumnGroups = (): ColumnGroup[] => [
  // Single columns without grouping
  {
    id: 'basic-info',
    Header: '', // Empty header for ungrouped columns
    columns: [
      {
        accessor: 'officeOfOrigin',
        Header: 'Office Of Origin',
      },
      {
        accessor: 'officeOfDestination', 
        Header: 'Office Of Destination',
      },
      {
        accessor: 'month',
        Header: 'Month',
      },
    ],
  },
  // Normal IBRS group
  {
    id: 'normal-ibrs',
    Header: 'Normal IBRS',
    columns: [
      {
        accessor: 'normalNetweightFormatted',
        Header: 'Net Weight kg',
      },
      {
        accessor: 'normalItemsFormatted',
        Header: 'Number of items',
      },
    ],
  },
  // Bulky IBRS group
  {
    id: 'bulky-ibrs',
    Header: 'Bulky IBRS',
    columns: [
      {
        accessor: 'bulkyNetWeightFormatted',
        Header: 'Net Weight kg',
      },
      {
        accessor: 'bulkyItemsFormatted',
        Header: 'Number of items',
      },
    ],
  },
  // Total IBRS group
  {
    id: 'total-ibrs',
    Header: 'Total IBRS',
    columns: [
      {
        accessor: 'totalnetWeightFormatted',
        Header: 'Net Weight kg',
      },
      {
        accessor: 'totalItemsFormatted',
        Header: 'Number of items',
      },
    ],
  },
];

function CN10Table({
  data = [],
  filename,
  designatedOperator,
  mailCategory,
  totalData,
  isLoading = false,
  rowBackgroundColor,
}: CN10TableProps): React.ReactElement {
  const columnGroups = React.useMemo(() => createCN10ColumnGroups(), []);

  // Create summary rows if totalData is provided
  const summaryRows = React.useMemo(() => {
    if (!totalData) return undefined;

    return {
      subTotalRow: {
        // Map the formatted total values to their respective column accessors
        normalNetweightFormatted: totalData.normalNetweightFormatted,
        normalItemsFormatted: totalData.normalItemsFormatted,
        bulkyNetWeightFormatted: totalData.bulkyNetWeightFormatted,
        bulkyItemsFormatted: totalData.bulkyItemsFormatted,
        totalnetWeightFormatted: totalData.totalnetWeightFormatted,
        totalItemsFormatted: totalData.totalItemsFormatted,
      }
    };
  }, [totalData]);

  return (
    <div data-testid="cn10-table-container">
      <GroupedTable
        data={data}
        columnGroups={columnGroups}
        moduleName={`CN10-${mailCategory || 'IBRS'}`}
        viewMode={true}
        disableAutoSizing={false}
        pageSize={25}
        lazyLoad={data.length > 50}
        lazyLoadHeight={400}
        lazyLoadingBatchSize={25}
        isLoading={isLoading}
        rowBackgroundColor={rowBackgroundColor}
        summaryRows={summaryRows}
        data-testid="cn10-grouped-table"
      />
    </div>
  );
}

export default CN10Table; 